import { useMemo, useState } from 'react'
import { useActivityLog, type ActivityEntity } from '../hooks/useActivityLog'
import { Card, Skeleton, Chip, type ChipTone } from '../components/ui'
import { BackLink } from '../components/BackLink'
import { formatMoney } from '../lib/format'
import type { ActivityLog } from '../lib/database.types'

const FILTERS: Array<{ value: ActivityEntity | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'sale', label: 'Sales' },
  { value: 'print', label: 'Prints' },
  { value: 'maintenance_event', label: 'Maintenance' },
]

export default function Activity() {
  const [filter, setFilter] = useState<ActivityEntity | 'all'>('all')
  const { data: log, isLoading } = useActivityLog({
    limit: 200,
    entityType: filter === 'all' ? undefined : filter,
  })

  const grouped = useMemo(() => {
    const groups: Record<string, ActivityLog[]> = {}
    for (const row of log ?? []) {
      const day = row.created_at.slice(0, 10) // YYYY-MM-DD
      if (!groups[day]) groups[day] = []
      groups[day].push(row)
    }
    return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a))
  }, [log])

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 720 }}>
      <BackLink fallback="/" />
      <h1 style={{ marginBottom: 'var(--space-4)' }}>Activity</h1>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-4)' }}>
        Append-only log of every meaningful change. Sales, prints, refunds, maintenance — what
        happened and when.
      </p>

      <div
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        {FILTERS.map((f) => {
          const active = f.value === filter
          return (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              style={{
                padding: 'var(--space-1) var(--space-3)',
                borderRadius: 'var(--radius-pill)',
                border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
                background: active ? 'var(--color-primary-soft)' : 'transparent',
                color: active ? 'var(--color-primary-text)' : 'var(--color-text-muted)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: active ? 700 : 500,
                cursor: 'pointer',
              }}
              aria-pressed={active}
            >
              {f.label}
            </button>
          )
        })}
      </div>

      {isLoading ? (
        <>
          <Skeleton height={60} style={{ marginBottom: 8 }} />
          <Skeleton height={60} style={{ marginBottom: 8 }} />
          <Skeleton height={60} />
        </>
      ) : grouped.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: 'var(--space-4)' }}>
            No activity yet.
          </div>
        </Card>
      ) : (
        <div>
          {grouped.map(([day, rows]) => (
            <div key={day} style={{ marginBottom: 'var(--space-6)' }}>
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-faint)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 700,
                  marginBottom: 'var(--space-2)',
                }}
              >
                {formatDayHeading(day)}
              </div>
              {rows.map((r) => (
                <ActivityRow key={r.id} row={r} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ActivityRow({ row }: { row: ActivityLog }) {
  const { tone, icon, summary } = describeRow(row)
  return (
    <Card padding="var(--space-3)" style={{ marginBottom: 'var(--space-2)' }}>
      <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
        <span style={{ fontSize: '1.25rem' }}>{icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>{summary}</div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
            {formatTime(row.created_at)}
          </div>
        </div>
        <Chip tone={tone}>{row.action}</Chip>
      </div>
    </Card>
  )
}

function describeRow(row: ActivityLog): { tone: ChipTone; icon: string; summary: string } {
  const meta = row.metadata as Record<string, unknown>
  const money = (v: unknown) => formatMoney(Number(v ?? 0))

  if (row.entity_type === 'sale') {
    const gross = meta.gross_amount
    const platform = meta.platform ?? 'sale'
    const customer = meta.customer_name
    const units = meta.units
    const who = customer ? ` to ${customer}` : ''
    const qty = typeof units === 'number' && units > 0 ? ` (${units} unit${units === 1 ? '' : 's'})` : ''
    switch (row.action) {
      case 'created':
        return { tone: 'success', icon: '💵', summary: `Sale logged: ${money(gross)} on ${platform}${who}${qty}` }
      case 'deleted':
        return { tone: 'danger', icon: '🗑️', summary: `Sale deleted: ${money(gross)} on ${platform}${who}` }
      case 'refunded':
        return { tone: 'warning', icon: '↩️', summary: `Refund: ${money(gross)} on ${platform}${who}` }
      case 'unrefunded':
        return { tone: 'default', icon: '↪️', summary: `Refund undone: ${money(gross)} on ${platform}${who}` }
      default:
        return { tone: 'default', icon: '💵', summary: `Sale ${row.action}` }
    }
  }

  if (row.entity_type === 'maintenance_event') {
    const task = meta.task_type ?? 'maintenance'
    const cost = meta.cost ?? 0
    const costLabel = Number(cost) > 0 ? ` · ${money(cost)}` : ''
    switch (row.action) {
      case 'created':
        return { tone: 'accent', icon: '🔧', summary: `Maintenance logged: ${task}${costLabel}` }
      case 'deleted':
        return { tone: 'danger', icon: '🗑️', summary: `Maintenance deleted: ${task}` }
      default:
        return { tone: 'default', icon: '🔧', summary: `Maintenance ${row.action}` }
    }
  }

  if (row.entity_type === 'print') {
    const printer = meta.printer_name ?? 'printer'
    const units = meta.units_produced ?? 0
    switch (row.action) {
      case 'created':
        return { tone: 'accent', icon: '🖨️', summary: `Print logged on ${printer} (${units} units)` }
      case 'deleted':
        return { tone: 'danger', icon: '🗑️', summary: `Print deleted (was on ${printer})` }
      default:
        return { tone: 'default', icon: '🖨️', summary: `Print ${row.action}` }
    }
  }

  return { tone: 'default', icon: '•', summary: `${row.entity_type} ${row.action}` }
}

function formatDayHeading(day: string): string {
  // Compute today/yesterday using LOCAL date components, not toISOString()
  // (which is UTC-based). A user in PST late at night would otherwise see
  // their own entries from earlier today get labeled with a weekday instead
  // of "Today" because UTC has already rolled to tomorrow.
  const todayLocal = localDateStr(new Date())
  if (day === todayLocal) return 'Today'
  const y2 = new Date()
  y2.setDate(y2.getDate() - 1)
  if (day === localDateStr(y2)) return 'Yesterday'
  const [y, m, d] = day.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

function localDateStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function formatTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}
